<template>
    <div class="mb-3 video-container" ref="videoColumnRef">
        <div v-if="loading"
             class="camera-loading-container d-flex flex-column justify-content-center align-items-center">
            <b-spinner />
            <div class="camera-access-text mt-3">
                {{ $t('sign_feedback.direct_record.camera_access') }}
            </div>
        </div>
        <video
            ref="videoRef"
            class="input_video"
            :width="videoColumnRef?.clientWidth || 0"
            :class="{ invisible: loading || (recordedVideoUrl && !countdown && !saveLandmarks && !isCheckingPosition) }"
        ></video>
        <canvas
            ref="canvasRef"
            class="output_canvas"
            :class="{ invisible: loading || (recordedVideoUrl && !countdown && !saveLandmarks && !isCheckingPosition) }"
        ></canvas>

        <div class="countdown-timer mt-n1 box" v-if="mediaPipeIsLoaded && (!recordedVideoUrl || countdown || saveLandmarks || isCheckingPosition)"
             :style="{width:videoRef?.clientWidth+'px'}">
            <div class="countdown-timer-bar" :class="{'animate':saveLandmarks}"
                 :style="{width:videoRef?.clientWidth+'px'}"></div>
        </div>

        <div v-if="countdown"
             class="countdown h-100 d-flex justify-content-center align-items-center"
             :style="{width:videoColumnRef?.clientWidth || 0 +'px'}">
            <Countdown @done="countdownDone"></Countdown>
        </div>

        <ReplayOverlay
            :show="!!(recordedVideoUrl && !saveLandmarks && !countdown && !isCheckingPosition)"
            :recordedReplayReady="recordedReplayReady"
            :recordedReplayPlaying="recordedReplayPlaying"
            :recordedReplayPrepPercent="recordedReplayPrepPercent"
            @play-single="startRecordedReplay"
            @recorded-replay-canvas-ready="onRecordedReplayCanvasReady"
            @overlay-click="handleReplayOverlayClick"
        />

        <div v-if="saveLandmarks" class="record-sign d-flex align-items-center">
            <font-awesome-icon
                class="text-danger mx-1"
                size="xs"
                :icon="['fas', 'circle']" />
            {{ $t('sign_feedback.step2.recording') }}
        </div>

        <PositionOverlay
            :checking="isCheckingPosition"
            :valid="isPositioningValid"
            :message="positioningFeedback"
            :showHint="(!loading && !saveLandmarks && !countdown && !isCheckingPosition && !recordedVideoUrl)"
            @cancel="cancelPositioning"
        />

        <video v-if="recordedVideoUrl" ref="postProcessVideoRef" :src="recordedVideoUrl" playsinline muted style="display:none;"></video>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import * as Camera from '@mediapipe/camera_utils';
import * as Holistic from '@mediapipe/holistic';
import Countdown from '@/components/signFeedback/Countdown.vue';
import ReplayOverlay from '@/components/signFeedback/ReplayOverlay.vue';
import PositionOverlay from '@/components/signFeedback/PositionOverlay.vue';
import { frameToResults } from '@/components/signFeedback/utils/LandmarksMapper';
import { drawOnCanvas } from '@/components/signFeedback/utils/SkeletonRenderer';
import { validate as validatePositioning } from '@/components/signFeedback/utils/PositioningValidator';
import { startFrameCapture, FrameCaptureController } from '@/components/signFeedback/services/FrameCaptureService';
import { startRecording, RecordingController } from '@/components/signFeedback/services/RecordingService';
import { singleFrameCollect } from '@/components/signFeedback/services/PostProcessingService';

@Component({ components: { Countdown, ReplayOverlay, PositionOverlay } })
export default class DirectRecordStage extends Vue {
    @Prop() holistic: any;
    @Prop({ type: Boolean, default: false }) skipPositioningCheck!: boolean;

    public videoColumnRef: any = null;
    public canvasRef: any = null;
    public videoRef: any = null;
    public postProcessVideoRef: any = null;

    public loading = true;
    public mediaPipeIsLoaded = false;
    public holisticPrewarmed = false;
    public recordingTimeMS = 4000;
    public camera: any = null;

    public saveLandmarks = false;
    public firstRecordingDone = false;
    public countdown = false;
    public isPositioningValid = false;
    public positioningFeedback = '';
    public isCheckingPosition = false;

    public capturedFrameTimestamps: number[] = [];
    public frameCaptureController: FrameCaptureController | null = null;

    public recordingController: RecordingController | null = null;
    public recordedVideoUrl: string | null = null;

    public isPostProcessing = false;
    public postProcessingDone = false;
    public postProcessingFramesProcessed = 0;
    public postProcessedLandmarks: any[] = [];

    public recordedReplayPlaying = false;
    public recordedReplayCanvasRef: any = null;
    public recordedReplayReady = false;
    public recordedReplayProcessedCount = 0;
    public recordedReplayTotalCount = 0;

    public recordedReplayFramesData: Array<{ bitmap: any, frameLandmarks: number[] }> = [];
    public recordedReplayTimerId: number | null = null;
    public recordedReplayDisplayIndex = 0;

    public componentActive = true;
    public positioningIntervalId: number | null = null;
    public postProcessCancel: () => boolean = () => false;
    public positioningSuccessTimeoutId: number | null = null;
    public positioningSuccessHold = false;

    get recordedReplayPrepPercent(): number | null {
        if (!this.recordedReplayTotalCount || this.recordedReplayTotalCount <= 0) return null;
        const pct = Math.floor((this.recordedReplayProcessedCount / this.recordedReplayTotalCount) * 100);
        return Math.max(0, Math.min(100, pct));
    }

    public mounted() {
        this.videoColumnRef = this.$refs['videoColumnRef'];
        this.canvasRef = this.$refs['canvasRef'];
        this.videoRef = this.$refs['videoRef'];
        this.canvasRef.width = this.videoColumnRef.clientWidth;

        this.camera = new Camera.Camera(this.videoRef, {
            onFrame: async () => {
                if (!this.componentActive || !this.holistic) return;
                const shouldSend =
                    this.isCheckingPosition ||
                    (!this.mediaPipeIsLoaded);
                if (shouldSend) {
                    try {
                        await this.holistic.send({ image: this.videoRef });
                    } catch (e) {
                        // Ignore errors if holistic was closed during navigation
                    }
                }
            },
        });
        this.camera.start()
            .then(() => {
                this.loading = false;
                this.holistic.onResults(this.onResults);
                this.canvasRef.width = this.videoRef.clientWidth;
                this.canvasRef.height = this.videoRef.clientHeight;
                this.clearCanvas();
                this.mediaPipeIsLoaded = true;
                this.emitState();
                this.prewarmHolistic();
            })
            .catch((err) => console.error('Failed to start camera with error: ', err));
    }

    public beforeDestroy() {
        this.componentActive = false;
        if (this.positioningIntervalId) {
            clearInterval(this.positioningIntervalId);
            this.positioningIntervalId = null;
        }
        this.postProcessCancel = () => true;
        if (this.camera) {
            try { this.camera.stop(); } catch (e) { /* noop */ }
            this.camera = null;
        }
        this.stopVideoStream();
        if (this.frameCaptureController) {
            try { this.frameCaptureController.stop(); } catch (e) { /* noop */ }
            this.frameCaptureController = null;
        }
        if (this.recordingController) {
            try { this.recordingController.stop(); } catch (e) { /* noop */ }
            this.recordingController = null;
        }
        if (this.recordedVideoUrl) {
            URL.revokeObjectURL(this.recordedVideoUrl);
            this.recordedVideoUrl = null;
        }
    }

    public deactivated() {
        this.componentActive = false;
        this.postProcessCancel = () => true;
        if (this.camera) {
            try { this.camera.stop(); } catch (e) { /* noop */ }
            this.camera = null;
        }
        this.stopVideoStream();
    }

    public startRecording() {
        this.cleanupRecordedVideoUrl();
        this.postProcessedLandmarks = [];
        this.postProcessingDone = false;
        this.postProcessingFramesProcessed = 0;
        this.isPostProcessing = false;
        this.recordedReplayReady = false;
        this.recordedReplayProcessedCount = 0;
        this.recordedReplayTotalCount = 0;
        this.emitState();
        if (this.skipPositioningCheck) {
            this.isCheckingPosition = false;
            this.countdown = true;
            this.emitState();
        } else {
            this.positioningFeedback = this.$t('sign_feedback.direct_record.positioning.checking') as string;
            this.isPositioningValid = false;
            this.isCheckingPosition = true;
            this.emitState();
            this.checkPositioning();
        }
    }

    private checkPositioning() {
        if (this.positioningIntervalId) {
            clearInterval(this.positioningIntervalId);
            this.positioningIntervalId = null;
        }
        this.positioningIntervalId = window.setInterval(() => {
            if (!this.componentActive) {
                if (this.positioningIntervalId) {
                    clearInterval(this.positioningIntervalId);
                    this.positioningIntervalId = null;
                }
                return;
            }
            if (this.isPositioningValid) {
                if (this.positioningIntervalId) {
                    clearInterval(this.positioningIntervalId);
                    this.positioningIntervalId = null;
                }
                this.positioningSuccessHold = true;
                if (this.positioningSuccessTimeoutId) {
                    clearTimeout(this.positioningSuccessTimeoutId);
                    this.positioningSuccessTimeoutId = null;
                }
                this.positioningSuccessTimeoutId = window.setTimeout(() => {
                    this.isCheckingPosition = false;
                    this.countdown = true;
                    this.positioningSuccessHold = false;
                    this.clearCanvas();
                    this.emitState();
                }, 1000) as unknown as number;
                this.emitState();
            }
        }, 100) as unknown as number;
    }

    public cancelPositioning() {
        this.isCheckingPosition = false;
        this.isPositioningValid = false;
        this.positioningFeedback = '';
        if (this.positioningIntervalId) {
            clearInterval(this.positioningIntervalId);
            this.positioningIntervalId = null;
        }
        if (this.positioningSuccessTimeoutId) {
            clearTimeout(this.positioningSuccessTimeoutId);
            this.positioningSuccessTimeoutId = null;
        }
        this.positioningSuccessHold = false;
        this.clearCanvas();
        this.emitState();
    }

    public countdownDone() {
        this.countdown = false;
        this.saveLandmarks = true;
        this.emitState();
        this.frameCaptureController = startFrameCapture(this.videoRef as HTMLVideoElement);
        const videoEl: any = this.videoRef as HTMLVideoElement;
        let stream: MediaStream | null = null;
        if (videoEl && videoEl.srcObject instanceof MediaStream) {
            stream = videoEl.srcObject as MediaStream;
        } else if (videoEl && typeof (videoEl as any).captureStream === 'function') {
            try { stream = (videoEl as any).captureStream(); } catch (e) { stream = null; }
        }
        // @ts-ignore
        if (!stream || typeof MediaRecorder === 'undefined') {
            console.warn('[DirectRecordStage] MediaRecorder or capture stream not available');
        } else {
            this.recordingController = startRecording(stream);
            if (!this.recordingController) {
                console.warn('[DirectRecordStage] Failed to create RecordingController');
            }
        }
        setTimeout(() => this.stopRecording(), this.recordingTimeMS);
    }

    public stopRecording() {
        this.firstRecordingDone = true;
        this.saveLandmarks = false;
        this.emitState();
        if (this.frameCaptureController) {
            const { timestampsMs } = this.frameCaptureController.stop();
            this.frameCaptureController = null;
            this.capturedFrameTimestamps = timestampsMs;
        }
        const finalize = async () => {
            if (this.recordingController) {
                try {
                    const { url } = await this.recordingController.stop();
                    this.recordingController = null;
                    this.cleanupRecordedVideoUrl();
                    this.recordedVideoUrl = url;
                    this.emitState();
                } catch (e) {
                    console.warn('[DirectRecordStage] Failed to stop RecordingController', e);
                }
            }
            this.$nextTick(() => {
                this.postProcessVideoRef = this.$refs['postProcessVideoRef'];
                if (this.recordedVideoUrl) {
                    this.prepareRecordedReplay();
                }
            });
        };
        finalize();
        this.clearCanvas();
    }

    public onResults(results: Holistic.Results) {
        if (!this.mediaPipeIsLoaded) {
            this.mediaPipeIsLoaded = true;
            this.emitState();
        }
        if (this.isCheckingPosition) {
            if (!this.positioningSuccessHold) {
                this.validatePositioning(results);
            }
        } else if (!this.saveLandmarks) {
            this.clearCanvas();
        }
    };

    private cleanupRecordedVideoUrl() {
        if (this.recordedVideoUrl) {
            URL.revokeObjectURL(this.recordedVideoUrl);
            this.recordedVideoUrl = null;
        }
    }

    private async prepareRecordedReplay() {
        this.postProcessVideoRef = this.$refs['postProcessVideoRef'] as HTMLVideoElement;
        const videoEl = this.postProcessVideoRef as HTMLVideoElement;
        if (!videoEl) return;
        const frameCount = Math.max(this.capturedFrameTimestamps?.length || 0, 1);
        const stepSec = (this.recordingTimeMS / frameCount) / 1000;
        const framesToSample: number[] = [];
        for (let i = 0; i < frameCount; i++) framesToSample.push(i * stepSec);
        this.recordedReplayTotalCount = framesToSample.length;
        this.recordedReplayProcessedCount = 0;

        const originalOnResults = this.onResults.bind(this);
        this.postProcessCancel = () => !this.componentActive;
        const { bitmaps, landmarks } = await singleFrameCollect(
            videoEl,
            framesToSample,
            this.holistic,
            originalOnResults,
            (processed) => { this.recordedReplayProcessedCount = processed; },
            () => this.postProcessCancel(),
        );

        this.recordedReplayFramesData = bitmaps.map((bitmap, i) => ({ bitmap, frameLandmarks: landmarks[i] || [] }));
        this.postProcessedLandmarks = landmarks.slice();
        this.isPostProcessing = false;
        this.postProcessingDone = this.postProcessedLandmarks.length > 0;
        this.$emit('postprocessed', this.postProcessedLandmarks);

        this.recordedReplayReady = true;
        this.emitState();
        await this.$nextTick();
        let canvas = this.recordedReplayCanvasRef as HTMLCanvasElement;
        if (!canvas) {
            canvas = await new Promise<HTMLCanvasElement | null>(resolve => {
                let attempts = 0;
                const poll = () => {
                    if (this.recordedReplayCanvasRef) return resolve(this.recordedReplayCanvasRef as HTMLCanvasElement);
                    const overlayEl = (this.$el as HTMLElement).querySelector('.replay-overlay');
                    const domCanvas = overlayEl ? overlayEl.querySelector('canvas.output_canvas') as HTMLCanvasElement : null;
                    if (domCanvas) return resolve(domCanvas);
                    if (attempts++ > 20) return resolve(null);
                    setTimeout(poll, 25);
                };
                poll();
            }) as HTMLCanvasElement | null;
            if (!canvas) return;
        }
        const width = this.videoRef?.clientWidth || videoEl.videoWidth || 640;
        const height = Math.round(width * (videoEl.videoHeight && videoEl.videoWidth ? (videoEl.videoHeight / videoEl.videoWidth) : (3/4)));
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;

        this.recordedReplayDisplayIndex = 0;
        if (this.recordedReplayTimerId) {
            clearTimeout(this.recordedReplayTimerId);
            this.recordedReplayTimerId = null;
        }
        const drawIndex = (idx: number) => {
            const data = this.recordedReplayFramesData[idx];
            if (!data) return;
            ctx.clearRect(0, 0, width, height);
            try { ctx.drawImage(data.bitmap, 0, 0, width, height); } catch {}
            const results = frameToResults(data.frameLandmarks.slice());
            drawOnCanvas(canvas, results, false);
        };
        const advance = () => {
            if (!this.recordedReplayPlaying) return;
            drawIndex(this.recordedReplayDisplayIndex);
            this.recordedReplayDisplayIndex += 1;
            if (this.recordedReplayDisplayIndex >= this.recordedReplayFramesData.length) {
                this.recordedReplayPlaying = false;
                this.recordedReplayDisplayIndex = 0;
                return;
            }
            const intervalMs = Math.max(1, Math.round(this.recordingTimeMS / this.recordedReplayFramesData.length));
            this.recordedReplayTimerId = window.setTimeout(advance, intervalMs) as unknown as number;
        };
        drawIndex(0);
        this.recordedReplayPlaying = false;
        this.recordedReplayTimerId = null;
        this.startRecordedReplay = () => {
            if (this.recordedReplayTimerId) {
                clearTimeout(this.recordedReplayTimerId);
                this.recordedReplayTimerId = null;
            }
            this.recordedReplayPlaying = true;
            this.recordedReplayDisplayIndex = 0;
            advance();
        };
    }

    private validatePositioning(results: Holistic.Results) {
        const verdict = validatePositioning(results);
        this.isPositioningValid = verdict.valid;
        this.positioningFeedback = this.$t(verdict.messageKey) as string;
    }

    private clearCanvas() {
        if (this.canvasRef) {
            const canvasCtx = this.canvasRef.getContext('2d')!;
            canvasCtx.clearRect(
                0,
                0,
                this.canvasRef.width,
                this.canvasRef.height,
            );
        }
    }

    public startRecordedReplay: () => void = () => {};

    public handleReplayOverlayClick(e: MouseEvent) {
        const target = e.target as HTMLElement;
        if (target && target.closest && target.closest('.replay-overlay__play')) return;
        if (this.recordedReplayReady && !this.recordedReplayPlaying) {
            this.startRecordedReplay();
        }
    }

    public onRecordedReplayCanvasReady = (el: HTMLCanvasElement) => {
        this.recordedReplayCanvasRef = el;
    };

    public teardown() {
        this.componentActive = false;
        if (this.positioningIntervalId) {
            clearInterval(this.positioningIntervalId);
            this.positioningIntervalId = null;
        }
        this.postProcessCancel = () => true;
        if (this.recordedReplayTimerId) {
            clearTimeout(this.recordedReplayTimerId);
            this.recordedReplayTimerId = null;
        }
        if (this.frameCaptureController) {
            try { this.frameCaptureController.stop(); } catch (e) { /* noop */ }
            this.frameCaptureController = null;
        }
        if (this.recordingController) {
            try { this.recordingController.stop(); } catch (e) { /* noop */ }
            this.recordingController = null;
        }
        if (this.camera) {
            try { this.camera.stop(); } catch (e) { /* noop */ }
            this.camera = null;
        }
        this.stopVideoStream();
        this.cleanupRecordedVideoUrl();
    }

    public getPostProcessedLandmarks(): any[] {
        return this.postProcessedLandmarks;
    }

    private async prewarmHolistic() {
        if (this.holisticPrewarmed || !this.componentActive) return;
        try {
            const videoEl: HTMLVideoElement = this.videoRef as any;
            if (!videoEl) return;
            const ensureReady = () => new Promise<void>((resolve) => {
                if (videoEl.readyState >= 2 && (videoEl.videoWidth || 0) > 0) return resolve();
                const onLoaded = () => {
                    videoEl.removeEventListener('loadeddata', onLoaded);
                    resolve();
                };
                videoEl.addEventListener('loadeddata', onLoaded, { once: true });
            });
            await ensureReady();
            if (!this.componentActive) return;
            try {
                await this.holistic.send({ image: videoEl });
            } catch (e) {
                return;
            }
            this.holisticPrewarmed = true;
            this.emitState();
        } catch (e) {
            /* noop */
        }
    }

    private emitState() {
        this.$emit('state-change', {
            loading: this.loading,
            mediaPipeIsLoaded: this.mediaPipeIsLoaded,
            holisticPrewarmed: this.holisticPrewarmed,
            saveLandmarks: this.saveLandmarks,
            firstRecordingDone: this.firstRecordingDone,
            countdown: this.countdown,
            isCheckingPosition: this.isCheckingPosition,
            isPostProcessing: this.isPostProcessing,
            postProcessingDone: this.postProcessingDone,
            recordedVideoUrl: this.recordedVideoUrl,
            recordedReplayReady: this.recordedReplayReady,
        });
    }

    private stopVideoStream() {
        try {
            const videoEl: HTMLVideoElement | null = this.videoRef as any;
            if (!videoEl) return;
            if (typeof videoEl.pause === 'function') {
                try { videoEl.pause(); } catch (e) { /* noop */ }
            }
            const stream = (videoEl as any).srcObject as MediaStream | null;
            if (stream && typeof stream.getTracks === 'function') {
                try {
                    stream.getTracks().forEach(track => {
                        try { track.stop(); } catch (e) { /* noop */ }
                    });
                } catch (e) { /* noop */ }
            }
            try { (videoEl as any).srcObject = null; } catch (e) { /* noop */ }
        } catch (e) {
            /* noop */
        }
    }
}
</script>

<style lang="scss" scoped>
.box {
    position: relative;
}

.video-container {
    position: relative;
    overflow: hidden;
    border-radius: 8px;
}

.input_video {
    width: 100%;
    height: auto;
    border-radius: 8px;
    display: block;
}

.output_canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    pointer-events: none;
    object-fit: cover;
}

.countdown-timer, .countdown-timer-bar {
    height: 8px;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 0.15rem;
    margin-bottom: 0;
}

.countdown-timer-bar {
    background-color: #f3705d;
    transform-origin: left;
}

@keyframes countdown {
    to {
        transform: scaleX(0);
    }
}

.animate {
    animation: countdown 4s linear forwards;
}

.countdown {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    color: white;
    font-size: 3rem;
    font-weight: bold;
}

.record-sign {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(0, 0, 0, 0.9);
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: bold;
}

.camera-loading-container {
    background-color: #E5ECFF;
    border-radius: 8px;
    min-height: 400px;
    width: 100%;
    aspect-ratio: 4/3;
}

.camera-access-text {
    color: #4a5568;
    font-size: 1rem;
    text-align: center;
    font-weight: 500;
}

.position-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 10;
}

.replay-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.replay-overlay__box {
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 8px;
}

.replay-overlay__progress {
    position: absolute;
    bottom: 0.5rem;
    right: 0.75rem;
    background: rgba(0,0,0,0.65);
    color: #fff;
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    font-size: 0.85rem;
}

.replay-overlay__play {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    border: none;
    background: rgba(0,0,0,0.45);
    color: #fff;
    cursor: pointer;
}
.replay-overlay__play:hover {
    background: rgba(0,0,0,0.6);
}
</style>

